// Leave Planning Modal - Ferienplaner style view
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { format, addMonths, startOfMonth, endOfMonth, eachDayOfInterval, parseISO, getDay, isSameDay } from 'date-fns';
import { fr, de, enUS } from 'date-fns/locale';
import { useLanguage } from '../contexts/LanguageContext';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { X, ChevronLeft, ChevronRight, CalendarDays, LayoutGrid, Rows3, Printer, Loader2 } from 'lucide-react';
import { VacancesIcon, MaladieIcon } from './AbsenceIcons';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from './ui/tooltip';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Work icon component - using favicon
function WorkIcon({ size = 14 }) {
  return (
    <img 
      src="/favicon-16x16.png" 
      alt="W"
      style={{ width: size, height: size, opacity: 0.7 }} 
    />
  );
}

// Day names short
const DAY_NAMES = {
  fr: ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'],
  de: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
  en: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
};

// Month names
const MONTH_NAMES = {
  fr: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'],
  de: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
};

export default function LeavePlanningModal({ open, onClose }) {
  const { language } = useLanguage();
  const locale = language === 'de' ? de : language === 'en' ? enUS : fr;
  
  const [startMonth, setStartMonth] = useState(() => startOfMonth(new Date()));
  const [viewMode, setViewMode] = useState('horizontal'); // 'horizontal' or 'vertical'
  const [team, setTeam] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [holidays, setHolidays] = useState({});
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const tableRef = useRef(null);
  const verticalRef = useRef(null);

  // Generate 12 months from startMonth
  const months = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => addMonths(startMonth, i));
  }, [startMonth]);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const startDate = format(startMonth, 'yyyy-MM-dd');
      const endDate = format(endOfMonth(addMonths(startMonth, 11)), 'yyyy-MM-dd');
      
      const [teamRes, absencesRes, scheduleRes, holidaysRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/absences`),
        axios.get(`${API}/schedule`, { params: { week_start: startDate, week_end: endDate } }),
        axios.get(`${API}/holidays`, { params: { year: startMonth.getFullYear() } })
      ]);
      
      // Also fetch holidays for next year if we span 2 years
      const endYear = addMonths(startMonth, 11).getFullYear();
      let allHolidays = holidaysRes.data || {};
      if (endYear !== startMonth.getFullYear()) {
        const nextYearHolidays = await axios.get(`${API}/holidays`, { params: { year: endYear } });
        allHolidays = { ...allHolidays, ...nextYearHolidays.data };
      }
      
      setTeam(teamRes.data.filter(u => u.is_active !== false));
      setAbsences(absencesRes.data.filter(a => a.status === 'approved'));
      setSchedule(scheduleRes.data || []);
      setHolidays(allHolidays);
    } catch (err) {
      console.error('Error fetching planning data:', err);
    } finally {
      setLoading(false);
    }
  }, [startMonth]);

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open, fetchData]);

  // Navigate months
  const handlePrevMonth = () => setStartMonth(prev => addMonths(prev, -1));
  const handleNextMonth = () => setStartMonth(prev => addMonths(prev, 1));
  
  // Month/Year selector
  const handleMonthChange = (value) => {
    const [year, month] = value.split('-').map(Number);
    setStartMonth(new Date(year, month, 1));
  };

  // Export to PDF
  const handleExportPDF = async () => {
    if (exporting) return;
    
    setExporting(true);
    try {
      const title = language === 'de' ? 'Ferienplaner' : language === 'en' ? 'Leave Planner' : 'Planning Congés';
      const generatedText = language === 'de' ? 'Erstellt am' : language === 'en' ? 'Generated on' : 'Généré le';
      
      if (viewMode === 'horizontal') {
        // HORIZONTAL VIEW - Single page PDF
        if (!tableRef.current) return;
        
        const canvas = await html2canvas(tableRef.current, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff'
        });
        
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        const pdfWidth = 420; // A3 width in mm
        const pdfHeight = (imgHeight * pdfWidth) / imgWidth;
        
        const pdf = new jsPDF({
          orientation: 'landscape',
          unit: 'mm',
          format: [pdfWidth, Math.max(pdfHeight + 40, 297)]
        });
        
        const period = `${monthNames[startMonth.getMonth()]} ${startMonth.getFullYear()} - ${monthNames[addMonths(startMonth, 11).getMonth()]} ${addMonths(startMonth, 11).getFullYear()}`;
        
        pdf.setFontSize(16);
        pdf.text(title, 10, 15);
        pdf.setFontSize(10);
        pdf.text(period, 10, 22);
        pdf.text(`${generatedText}: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, 10, 28);
        
        const imgData = canvas.toDataURL('image/png');
        pdf.addImage(imgData, 'PNG', 5, 35, pdfWidth - 10, pdfHeight - 10);
        
        pdf.save(`planning-conges-${format(startMonth, 'yyyy-MM')}.pdf`);
        
      } else {
        // VERTICAL VIEW - Multi-page PDF (one month per page)
        if (!verticalRef.current) return;
        
        const monthTables = verticalRef.current.querySelectorAll('[data-month-table]');
        if (monthTables.length === 0) return;
        
        const pdf = new jsPDF({
          orientation: 'landscape',
          unit: 'mm',
          format: 'a4'
        });
        
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        
        for (let i = 0; i < monthTables.length; i++) {
          if (i > 0) pdf.addPage();
          
          const monthTable = monthTables[i];
          const canvas = await html2canvas(monthTable, {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff'
          });
          
          // Add title on each page
          pdf.setFontSize(14);
          pdf.text(title, 10, 12);
          pdf.setFontSize(9);
          pdf.text(`${generatedText}: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, 10, 18);
          pdf.text(`Page ${i + 1} / ${monthTables.length}`, pageWidth - 30, 12);
          
          // Calculate image dimensions to fit page
          const imgWidth = canvas.width;
          const imgHeight = canvas.height;
          const availableWidth = pageWidth - 20;
          const availableHeight = pageHeight - 30;
          
          let finalWidth = availableWidth;
          let finalHeight = (imgHeight * availableWidth) / imgWidth;
          
          if (finalHeight > availableHeight) {
            finalHeight = availableHeight;
            finalWidth = (imgWidth * availableHeight) / imgHeight;
          }
          
          const imgData = canvas.toDataURL('image/png');
          pdf.addImage(imgData, 'PNG', 10, 25, finalWidth, finalHeight);
        }
        
        pdf.save(`planning-conges-${format(startMonth, 'yyyy-MM')}.pdf`);
      }
    } catch (error) {
      console.error('Error exporting PDF:', error);
    } finally {
      setExporting(false);
    }
  };

  // Get cell content for a user/day
  const getCellContent = (userId, dateStr) => {
    const date = parseISO(dateStr);
    const dayOfWeek = getDay(date);
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    
    // Check if has work schedule FIRST (even on weekends)
    const hasWork = schedule.some(s => s.user_id === userId && s.date === dateStr);
    if (hasWork) {
      return { type: 'work', icon: 'work', isWeekend };
    }
    
    // Check absences (can also be on weekends for some edge cases)
    const absence = absences.find(a => {
      if (a.user_id !== userId) return false;
      const start = parseISO(a.start_date);
      const end = parseISO(a.end_date);
      return date >= start && date <= end;
    });
    
    if (absence) {
      const isConge = absence.absence_type === 'conge' || absence.absence_type === 'paidLeave';
      const isMaladie = absence.absence_type === 'maladie' || absence.absence_type === 'sickLeave';
      return { 
        type: 'absence', 
        absenceType: absence.absence_type,
        isConge, 
        isMaladie,
        dayPart: absence.day_part,
        isWeekend
      };
    }
    
    // Check if weekend (after checking work and absences)
    if (isWeekend) {
      return { type: 'weekend', icon: null };
    }
    
    // Check if holiday (only for non-weekends that don't have work)
    const holiday = holidays[dateStr];
    if (holiday?.isNonWorked) {
      return { type: 'holiday', icon: null, name: holiday[language] || holiday.fr };
    }
    
    return { type: 'empty', icon: null };
  };

  // Calculate totals per user per month
  const calculateTotals = (userId, month) => {
    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(month);
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
    
    let conges = 0;
    let maladies = 0;
    
    days.forEach(day => {
      const dateStr = format(day, 'yyyy-MM-dd');
      const cell = getCellContent(userId, dateStr);
      if (cell.type === 'absence') {
        const factor = cell.dayPart === 'morning' || cell.dayPart === 'afternoon' ? 0.5 : 1;
        if (cell.isConge) conges += factor;
        if (cell.isMaladie) maladies += factor;
      }
    });
    
    return { conges, maladies };
  };

  // Generate year options (current year -2 to +2)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  const dayNames = DAY_NAMES[language] || DAY_NAMES.fr;
  const monthNames = MONTH_NAMES[language] || MONTH_NAMES.fr;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[98vw] w-[98vw] h-[95vh] max-h-[95vh] p-0 overflow-hidden">
        <TooltipProvider>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/50">
          <div className="flex items-center gap-4">
            <CalendarDays className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">
              {language === 'de' ? 'Ferienplaner' : language === 'en' ? 'Leave Planner' : 'Planning Congés'}
            </h2>
            
            {/* Month/Year Selector */}
            <div className="flex items-center gap-2 ml-4">
              <Button variant="outline" size="icon" onClick={handlePrevMonth}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              
              <Select 
                value={`${startMonth.getFullYear()}-${startMonth.getMonth()}`}
                onValueChange={handleMonthChange}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map(year => (
                    monthNames.map((monthName, monthIdx) => (
                      <SelectItem key={`${year}-${monthIdx}`} value={`${year}-${monthIdx}`}>
                        {monthName} {year}
                      </SelectItem>
                    ))
                  )).flat()}
                </SelectContent>
              </Select>
              
              <Button variant="outline" size="icon" onClick={handleNextMonth}>
                <ChevronRight className="h-4 w-4" />
              </Button>
              
              {/* View Mode Toggle */}
              <Tabs value={viewMode} onValueChange={setViewMode} className="ml-4">
                <TabsList className="h-8">
                  <TabsTrigger value="horizontal" className="h-7 px-2">
                    <LayoutGrid className="h-4 w-4 mr-1" />
                    {language === 'de' ? 'Horizontal' : language === 'en' ? 'Horizontal' : 'Horizontal'}
                  </TabsTrigger>
                  <TabsTrigger value="vertical" className="h-7 px-2">
                    <Rows3 className="h-4 w-4 mr-1" />
                    {language === 'de' ? 'Vertikal' : language === 'en' ? 'Vertical' : 'Vertical'}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              
              {/* PDF Export Button */}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleExportPDF}
                disabled={exporting || loading}
                className="ml-2"
                data-testid="export-pdf-btn"
              >
                {exporting ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Printer className="h-4 w-4 mr-1" />
                )}
                PDF
              </Button>
            </div>
          </div>
          
          {/* Legend */}
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1">
              <VacancesIcon size={14} />
              <span>{language === 'de' ? 'Ferien' : language === 'en' ? 'Leave' : 'Congés'}</span>
            </div>
            <div className="flex items-center gap-1">
              <MaladieIcon size={14} />
              <span>{language === 'de' ? 'Krankheit' : language === 'en' ? 'Sick' : 'Maladie'}</span>
            </div>
            <div className="flex items-center gap-1">
              <WorkIcon size={14} />
              <span>{language === 'de' ? 'Arbeit' : language === 'en' ? 'Work' : 'Travail'}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 bg-blue-100 dark:bg-blue-900 border border-blue-300 rounded"></div>
              <span>{language === 'de' ? 'Wochenende' : language === 'en' ? 'Weekend' : 'Weekend'}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 bg-amber-100 dark:bg-amber-900 border border-amber-300 rounded"></div>
              <span>{language === 'de' ? 'Feiertag' : language === 'en' ? 'Holiday' : 'Férié'}</span>
            </div>
          </div>
          
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-auto p-2">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : viewMode === 'horizontal' ? (
            /* HORIZONTAL VIEW - Original layout */
            <div className="overflow-x-auto" ref={tableRef}>
              <table className="border-collapse text-[10px]">
                <thead>
                  {/* Month row */}
                  <tr>
                    <th className="sticky left-0 z-20 bg-card border border-border px-2 py-1 font-semibold min-w-[120px]">
                      {language === 'de' ? 'Mitarbeiter' : language === 'en' ? 'Employee' : 'Collaborateur'}
                    </th>
                    {months.map((month, monthIdx) => {
                      const daysInMonth = eachDayOfInterval({ 
                        start: startOfMonth(month), 
                        end: endOfMonth(month) 
                      }).length;
                      return (
                        <th 
                          key={monthIdx} 
                          colSpan={daysInMonth + 1} 
                          className="border border-border px-1 py-1 bg-primary/10 text-primary font-semibold"
                        >
                          {monthNames[month.getMonth()]} {month.getFullYear()}
                        </th>
                      );
                    })}
                  </tr>
                  {/* Day numbers row */}
                  <tr>
                    <th className="sticky left-0 z-20 bg-card border border-border"></th>
                    {months.map((month, monthIdx) => {
                      const days = eachDayOfInterval({ start: startOfMonth(month), end: endOfMonth(month) });
                      return (
                        <>
                          {days.map((day, dayIdx) => {
                            const dow = getDay(day);
                            const isWeekend = dow === 0 || dow === 6;
                            return (
                              <th 
                                key={`${monthIdx}-${dayIdx}`}
                                className={`border border-border px-0.5 py-0.5 min-w-[22px] font-normal ${
                                  isWeekend ? 'bg-blue-100 dark:bg-blue-900/50' : ''
                                }`}
                              >
                                <div className="flex flex-col items-center">
                                  <span className="text-[8px] text-muted-foreground">{dayNames[dow]}</span>
                                  <span>{day.getDate()}</span>
                                </div>
                              </th>
                            );
                          })}
                          {/* Total column for month */}
                          <th className="border border-border px-1 py-0.5 bg-muted/50 min-w-[30px] font-semibold">
                            Σ
                          </th>
                        </>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {team.map((member, memberIdx) => {
                    return (
                      <tr key={member.id} className={memberIdx % 2 === 0 ? 'bg-card' : 'bg-muted/20'}>
                        {/* Employee name */}
                        <td className="sticky left-0 z-10 border border-border px-2 py-1 font-medium whitespace-nowrap bg-inherit">
                          <div className="flex items-center gap-1">
                            <div 
                              className="w-2 h-2 rounded-full flex-shrink-0" 
                              style={{ backgroundColor: member.color || '#3B82F6' }}
                            />
                            {member.first_name} {member.last_name?.charAt(0)}.
                          </div>
                        </td>
                        {/* Days */}
                        {months.map((month, monthIdx) => {
                          const days = eachDayOfInterval({ start: startOfMonth(month), end: endOfMonth(month) });
                          const totals = calculateTotals(member.id, month);
                          
                          return (
                            <>
                              {days.map((day, dayIdx) => {
                                const dateStr = format(day, 'yyyy-MM-dd');
                                const cell = getCellContent(member.id, dateStr);
                                
                                let bgColor = '';
                                let content = null;
                                
                                if (cell.type === 'weekend') {
                                  bgColor = 'bg-blue-100 dark:bg-blue-900/50';
                                } else if (cell.type === 'holiday') {
                                  bgColor = 'bg-amber-100 dark:bg-amber-900/50';
                                  content = (
                                    <Tooltip>
                                      <TooltipTrigger>
                                        <span className="text-[8px] text-amber-700">F</span>
                                      </TooltipTrigger>
                                      <TooltipContent>{cell.name}</TooltipContent>
                                    </Tooltip>
                                  );
                                } else if (cell.type === 'absence') {
                                  if (cell.isConge) {
                                    bgColor = 'bg-green-100 dark:bg-green-900/50';
                                    content = <VacancesIcon size={12} />;
                                  } else if (cell.isMaladie) {
                                    bgColor = 'bg-red-100 dark:bg-red-900/50';
                                    content = <MaladieIcon size={12} />;
                                  } else {
                                    bgColor = 'bg-gray-100 dark:bg-gray-800';
                                    content = <span className="text-[8px]">A</span>;
                                  }
                                } else if (cell.type === 'work') {
                                  // Si travail le week-end, garder le fond bleu
                                  if (cell.isWeekend) {
                                    bgColor = 'bg-blue-100 dark:bg-blue-900/50';
                                  }
                                  content = <WorkIcon size={12} />;
                                }
                                
                                return (
                                  <td 
                                    key={`${monthIdx}-${dayIdx}`}
                                    className={`border border-border/50 px-0 py-0 text-center ${bgColor}`}
                                    style={{ minWidth: '22px', height: '22px' }}
                                  >
                                    {content}
                                  </td>
                                );
                              })}
                              {/* Monthly total */}
                              <td className="border border-border px-1 py-0.5 text-center bg-muted/50 font-medium">
                                {totals.conges > 0 && (
                                  <span className="text-green-700 dark:text-green-400">{totals.conges}</span>
                                )}
                                {totals.conges > 0 && totals.maladies > 0 && '/'}
                                {totals.maladies > 0 && (
                                  <span className="text-red-700 dark:text-red-400">{totals.maladies}</span>
                                )}
                                {totals.conges === 0 && totals.maladies === 0 && '-'}
                              </td>
                            </>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            /* VERTICAL VIEW - Months stacked vertically */
            <div className="space-y-6" ref={verticalRef}>
              {months.map((month, monthIdx) => {
                const days = eachDayOfInterval({ start: startOfMonth(month), end: endOfMonth(month) });
                
                return (
                  <div key={monthIdx} className="border border-border rounded-lg overflow-hidden" data-month-table>
                    {/* Month header */}
                    <div className="bg-primary/10 px-3 py-2 border-b border-border">
                      <h3 className="font-semibold text-primary">
                        {monthNames[month.getMonth()]} {month.getFullYear()}
                      </h3>
                    </div>
                    
                    {/* Month table */}
                    <div className="overflow-x-auto">
                      <table className="border-collapse text-[10px] w-full">
                        <thead>
                          <tr>
                            <th className="sticky left-0 z-20 bg-card border-r border-border px-2 py-1 font-semibold min-w-[120px] text-left">
                              {language === 'de' ? 'Mitarbeiter' : language === 'en' ? 'Employee' : 'Collaborateur'}
                            </th>
                            {days.map((day, dayIdx) => {
                              const dow = getDay(day);
                              const isWeekend = dow === 0 || dow === 6;
                              const dateStr = format(day, 'yyyy-MM-dd');
                              const holiday = holidays[dateStr];
                              const isHoliday = holiday?.isNonWorked;
                              
                              return (
                                <th 
                                  key={dayIdx}
                                  className={`border-r border-border px-0.5 py-1 min-w-[24px] font-normal text-center ${
                                    isWeekend ? 'bg-blue-100 dark:bg-blue-900/50' : 
                                    isHoliday ? 'bg-amber-100 dark:bg-amber-900/50' : ''
                                  }`}
                                >
                                  <div className="flex flex-col items-center">
                                    <span className="text-[8px] text-muted-foreground">{dayNames[dow]}</span>
                                    <span className="font-medium">{day.getDate()}</span>
                                  </div>
                                </th>
                              );
                            })}
                            {/* Total column */}
                            <th className="border-l border-border px-2 py-1 bg-muted/50 min-w-[40px] font-semibold text-center">
                              Σ
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {team.map((member, memberIdx) => {
                            const totals = calculateTotals(member.id, month);
                            
                            return (
                              <tr key={member.id} className={memberIdx % 2 === 0 ? 'bg-card' : 'bg-muted/20'}>
                                {/* Employee name */}
                                <td className="sticky left-0 z-10 border-r border-t border-border px-2 py-1 font-medium whitespace-nowrap bg-inherit">
                                  <div className="flex items-center gap-1">
                                    <div 
                                      className="w-2 h-2 rounded-full flex-shrink-0" 
                                      style={{ backgroundColor: member.color || '#3B82F6' }}
                                    />
                                    {member.first_name} {member.last_name?.charAt(0)}.
                                  </div>
                                </td>
                                {/* Days */}
                                {days.map((day, dayIdx) => {
                                  const dateStr = format(day, 'yyyy-MM-dd');
                                  const cell = getCellContent(member.id, dateStr);
                                  
                                  let bgColor = '';
                                  let content = null;
                                  
                                  if (cell.type === 'weekend') {
                                    bgColor = 'bg-blue-100 dark:bg-blue-900/50';
                                  } else if (cell.type === 'holiday') {
                                    bgColor = 'bg-amber-100 dark:bg-amber-900/50';
                                    content = (
                                      <Tooltip>
                                        <TooltipTrigger>
                                          <span className="text-[8px] text-amber-700">F</span>
                                        </TooltipTrigger>
                                        <TooltipContent>{cell.name}</TooltipContent>
                                      </Tooltip>
                                    );
                                  } else if (cell.type === 'absence') {
                                    if (cell.isConge) {
                                      bgColor = 'bg-green-100 dark:bg-green-900/50';
                                      content = <VacancesIcon size={12} />;
                                    } else if (cell.isMaladie) {
                                      bgColor = 'bg-red-100 dark:bg-red-900/50';
                                      content = <MaladieIcon size={12} />;
                                    } else {
                                      bgColor = 'bg-gray-100 dark:bg-gray-800';
                                      content = <span className="text-[8px]">A</span>;
                                    }
                                  } else if (cell.type === 'work') {
                                    // Si travail le week-end, garder le fond bleu
                                    if (cell.isWeekend) {
                                      bgColor = 'bg-blue-100 dark:bg-blue-900/50';
                                    }
                                    content = <WorkIcon size={12} />;
                                  }
                                  
                                  return (
                                    <td 
                                      key={dayIdx}
                                      className={`border-r border-t border-border/50 px-0 py-0 text-center ${bgColor}`}
                                      style={{ minWidth: '24px', height: '24px' }}
                                    >
                                      {content}
                                    </td>
                                  );
                                })}
                                {/* Monthly total */}
                                <td className="border-l border-t border-border px-1 py-0.5 text-center bg-muted/50 font-medium">
                                  {totals.conges > 0 && (
                                    <span className="text-green-700 dark:text-green-400">{totals.conges}</span>
                                  )}
                                  {totals.conges > 0 && totals.maladies > 0 && '/'}
                                  {totals.maladies > 0 && (
                                    <span className="text-red-700 dark:text-red-400">{totals.maladies}</span>
                                  )}
                                  {totals.conges === 0 && totals.maladies === 0 && '-'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        </TooltipProvider>
      </DialogContent>
    </Dialog>
  );
}
